import { useState } from "react";
import { eventFormDates } from "@/lib/eventDates";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormSection } from "@/components/ui/form-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Image as ImageIcon, CircleDot } from "lucide-react";
import { useCreateEvent } from "@/hooks/useEvents";
import { useCategories } from "@/hooks/useCategories";
import { useVenues } from "@/hooks/useVenues";
import { toast } from "sonner";
import type { EventStatus, EventResponse } from "@/types";
import { EventImageManager } from "./EventImageManager";
import { EventFormFields } from "./EventFormFields";
import { emptyEventFormValues, type EventFormValues } from "./eventFormValues";

const EVENT_STATUSES: { value: EventStatus; label: string }[] = [
  { value: "approved", label: "Zatwierdzone" },
  { value: "pending", label: "Oczekujące" },
  { value: "rejected", label: "Odrzucone" },
];

interface CreateEventDialogProps {
  /** Controlled open state. When provided, the dialog is driven externally
      (e.g. top-bar quick-add or ⌘K) and the default trigger button is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateEventDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateEventDialogProps = {}) {
  const createEvent = useCreateEvent();
  const { data: categories = [] } = useCategories();
  const { data: venues = [] } = useVenues();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [form, setForm] = useState<EventFormValues>(emptyEventFormValues());
  const [status, setStatus] = useState<EventStatus>("approved");
  const [createdEvent, setCreatedEvent] = useState<EventResponse | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isControlled) controlledOnOpenChange?.(isOpen);
    else setUncontrolledOpen(isOpen);
    if (!isOpen) {
      setCreatedEvent(null);
      setForm(emptyEventFormValues());
      setStatus("approved");
    }
  };

  const handleChange = (patch: Partial<EventFormValues>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dates = eventFormDates(form);
      if (!dates.date) throw new Error("Podaj datę rozpoczęcia.");
      const result = await createEvent.mutateAsync({
        title: form.title,
        eventDescription: form.eventDescription,
        date: dates.date,
        endDate: dates.endDate,
        isAllDay: form.isAllDay,
        venueId: form.venueId,
        categoryId: form.categoryId,
        detailsUrl: form.detailsUrl,
        registrationOptions: form.registrationOptions,
        registrationNotes: form.registrationNotes,
        source: form.source,
        sourceUrl: form.sourceUrl,
        status,
        isPreview: form.isPreview,
      });
      toast.success("Wydarzenie dodane");
      setCreatedEvent(result);
    } catch (err) {
      toast.error("Nie udało się dodać wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Dodaj wydarzenie
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden bg-pulso-bg p-0">
        <DialogHeader className="border-b border-black/5 bg-pulso-surface px-6 py-4 text-left">
          <DialogTitle className="font-brand text-xl">
            {createdEvent ? "Dodaj zdjęcia" : "Nowe wydarzenie"}
          </DialogTitle>
        </DialogHeader>
        {createdEvent ? (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              <p className="text-sm text-pulso-muted">
                Wydarzenie zostało utworzone. Możesz teraz dodać zdjęcia.
              </p>
              <FormSection icon={ImageIcon} title="Zdjęcia">
                <EventImageManager eventId={createdEvent.id} />
              </FormSection>
            </div>
            <DialogFooter className="border-t border-black/5 bg-pulso-surface px-6 py-4">
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Gotowe
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <form
              id="event-create-form"
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto p-6"
            >
              <EventFormFields
                values={form}
                onChange={handleChange}
                categories={categories}
                venues={venues}
                showPreviewToggle
                afterFields={
                  <FormSection icon={CircleDot} title="Status">
                    <Select value={status} onValueChange={(v) => setStatus(v as EventStatus)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormSection>
                }
              />
            </form>
            <DialogFooter className="border-t border-black/5 bg-pulso-surface px-6 py-4">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Anuluj
              </Button>
              <Button
                type="submit"
                form="event-create-form"
                disabled={createEvent.isPending || !form.venueId || !form.categoryId}
              >
                {createEvent.isPending ? "Dodawanie..." : "Utwórz wydarzenie"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
